import React, { useMemo, useState } from 'react';
import { Tooltip } from 'recharts';
import { InfoAlert } from 'src/components/atoms/alerts/InfoAlert';
import { ResponseCurveTooltipContent } from 'src/components/atoms/tooltips/ResponseCurveTooltipContent';
import {
  DataBox,
  DataBoxBaseProps,
  DataBoxLegendProps,
} from 'src/components/molecules/DataBox';
import {
  AreaScatterGraph,
  AreaScatterDataType,
} from 'src/components/molecules/graphs/AreaScatterGraph';
import { useCustomerSettingsContext } from 'src/contexts/CustomerSettingsContext';
import { useFilterContext } from 'src/contexts/FilterContext';
import { getTimeMultiplier } from 'src/features/MM/hooks/useTimeFreqMultiplier';
import { useAggregationLevelContext } from '../../contexts/AggregationLevelContext';
import { useChartEffectTickFormatter } from 'src/hooks/useEffectFormatter';
import { ResponseCurveDatapoint } from 'src/types';
import { currencyValueTickFormatter } from 'src/utils/TickFormatters';
import { useCurrency } from 'src/hooks/useCurrency';
import { responseCurvesDef } from 'src/utils/metricDefinitions';
import { useKpiMetricLabels } from 'src/hooks/useKpiMetricLabels';
import { generateResponseCurveDatapoints } from 'src/utils/responseCurve';
import { useMMDataContext } from '../../contexts/MMDataContext';

export const ResponseCurvesBox: React.FC<DataBoxBaseProps> = ({ ...props }) => {
  const { getMediaName } = useCustomerSettingsContext();
  const { responseCurve } = useMMDataContext();
  const { mediaColorMap } = useFilterContext();
  const { code } = useCurrency();
  const { metricLabel } = useKpiMetricLabels();

  const [infoOpen, setInfoOpen] = useState(false);
  const { aggregateLevel } = useAggregationLevelContext();
  const valueMultiplier = getTimeMultiplier('weekly', aggregateLevel);
  const isLoading = responseCurve.isLoading;
  const isError = responseCurve.isError;

  const ValueTickFormatter = useChartEffectTickFormatter();

  const dataKeys = {
    xAxis: 'spend',
    yAxis: 'response',
  };

  const data: AreaScatterDataType<ResponseCurveDatapoint>[] = useMemo(() => {
    const generatedData = generateResponseCurveDatapoints(
      responseCurve.data,
      600,
      valueMultiplier,
      1.3,
    );

    return generatedData.map((x) => ({
      name: getMediaName(x.channel),
      data: x.data,
      points: [
        {
          response: x.meanResponse,
          spend: x.meanSpend,
        },
      ],
    }));
  }, [responseCurve.data, valueMultiplier, getMediaName]);

  const legendProps: DataBoxLegendProps = {
    placement: 'right',
    lineKeys: data.map((d) => d.name),
    keyColorMap: mediaColorMap,
  };

  return (
    <>
      <InfoAlert
        headerText={props.title ?? ''}
        bodyText={responseCurvesDef(metricLabel)}
        isOpen={infoOpen}
        onClose={() => setInfoOpen(false)}
      />
      <DataBox
        {...props}
        onInfoButtonClick={() => setInfoOpen(true)}
        legendProps={legendProps}
        hasData={data.length > 0}
        isError={isError}
        infoNextToTitle={true}
        isLoading={isLoading}>
        <AreaScatterGraph
          xAxisLabel="Investment"
          yAxisLabel="Effect"
          data={data}
          dataKeys={dataKeys}
          xAxisFormatter={currencyValueTickFormatter(code)}
          yAxisFormatter={ValueTickFormatter}>
          <Tooltip
            offset={25}
            content={
              <ResponseCurveTooltipContent
                valueFormatter={ValueTickFormatter}
                responseCurveData={data}
              />
            }
          />
        </AreaScatterGraph>
      </DataBox>
    </>
  );
};
